<template>
  <div
    class="communication-status-panel"
    :class="{ 'communication-status-panel--stale': degraded }"
  >
    <panel-summary
      :summary="summary"
      :age-in-ms="ageInMs"
      :stale="degraded"
      :auto-scroll="autoScroll"
      :auto-scroll-available="!isRadial"
      :auto-scroll-status="autoScrollStatus"
      @update:auto-scroll="setAutoScroll"
    />

    <panel-controls
      :grouping="grouping"
      :chart="chart"
      :view="view"
      :states="states"
      :query="query"
      :matched-count="filtered.matchedAssetCount"
      @update:grouping="updateUrl({ groupBy: $event })"
      @update:chart="updateUrl({ chart: $event })"
      @update:view="updateUrl({ view: $event })"
      @update:states="updateUrl({ states: $event.join(',') })"
      @update:query="updateUrl({ q: $event })"
    />

    <div
      v-if="sessionExpired"
      class="communication-status-panel__notice communication-status-panel__notice--session"
    >
      <h2>{{ $t('communication-status-panel.session-expired') }}</h2>
      <p>{{ $t('communication-status-panel.session-expired-detail') }}</p>
    </div>

    <p v-else-if="degraded" class="communication-status-panel__notice">
      {{ $t('communication-status-panel.stale') }}
    </p>

    <main
      class="communication-status-panel__body"
      :class="{
        'communication-status-panel__body--chart': isRadial,
        'communication-status-panel__body--scroller': showGrid,
      }"
    >
      <loader v-if="initialLoading" class="communication-status-panel__loader" size="md" />

      <p v-else-if="!filtered.groups.length" class="communication-status-panel__empty">
        {{ $t('communication-status-panel.empty') }}
      </p>

      <panel-sunburst
        v-else-if="isRadial"
        :assets="filteredAssets"
        :changed-keys="changedKeys"
        :refresh-interval-in-seconds="refreshIntervalInSeconds"
        :root-level="sunburstRoot"
      />

      <panel-auto-scroll
        v-else
        :enabled="autoScroll"
        :layout-key="layoutKey"
        @status="onAutoScrollStatus"
      >
        <div class="communication-status-panel__grid" :class="gridClass">
          <group-card
            v-for="group in filtered.groups"
            :key="group.key"
            :group="group"
            :chart="chart"
            :hide-band-names="groupedByAsset"
          />
        </div>
      </panel-auto-scroll>
    </main>
  </div>
</template>

<script>
import Loader from '@/components/Loading';
import GroupCard from '@/domain/components/generalPanel/communicationStatus/panel/GroupCard';
import PanelAutoScroll from '@/domain/components/generalPanel/communicationStatus/panel/PanelAutoScroll';
import PanelControls from '@/domain/components/generalPanel/communicationStatus/panel/PanelControls';
import PanelSummary from '@/domain/components/generalPanel/communicationStatus/panel/PanelSummary';
import PanelSunburst from '@/domain/components/generalPanel/communicationStatus/panel/PanelSunburst';
import loadAllInstallations from '@/domain/services/generalPanel/communicationStatusPanel/panelDataLoader';
import {
  buildAssets,
  summarizeAssets,
} from '@/domain/services/generalPanel/communicationStatusPanel/panelAggregator';
import {
  groupAssets,
  groupingLevels,
  isValidGrouping,
  DEFAULT_GROUPING,
} from '@/domain/services/generalPanel/communicationStatusPanel/panelGrouping';
import applyPanelFilter, {
  normalizeStateFilter,
} from '@/domain/services/generalPanel/communicationStatusPanel/panelFilter';
import {
  diffStates,
  markChangedGroups,
  snapshotStates,
} from '@/domain/services/generalPanel/communicationStatusPanel/alarmPulse';
import { isStale } from '@/domain/services/generalPanel/communicationStatusPanel/dataAge';
import { isValidPanelChart, DEFAULT_PANEL_CHART } from '@/domain/enums/panelChart';
import { panelViews, isValidPanelView, DEFAULT_PANEL_VIEW } from '@/domain/enums/panelView';
import { normalizeSunburstRoot } from '@/domain/services/generalPanel/communicationStatusPanel/panelSunburst';

const CLOCK_INTERVAL_IN_MS = 1000;
const MS_IN_SECOND = 1000;
/** Valor de `?autoscroll=` que liga a Rolagem Automática. */
const AUTO_SCROLL_ON = 'on';

/** Normaliza um parâmetro de rota que pode vir ausente, único ou repetido. */
function toArrayParam(value) {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  return Array.isArray(value) ? value : [value];
}

export default {
  name: 'CommunicationStatusPanel',
  components: {
    GroupCard,
    Loader,
    PanelAutoScroll,
    PanelControls,
    PanelSummary,
    PanelSunburst,
  },
  data: () => ({
    assets: [],
    summary: {},
    lastUpdate: null,
    // Retrato do ciclo anterior, para saber o que mudou neste.
    previousStates: null,
    changedKeys: [],
    now: Date.now(),
    initialLoading: true,
    sessionExpired: false,
    refreshTimer: null,
    clockTimer: null,
    // Emitido pela Rolagem Automática, que fica na grade, e lido pelo selo de
    // página, que fica no Placar.
    autoScrollStatus: { running: false },
  }),
  computed: {
    /**
     * Escopo fixado na URL: quem pendura a TV salva o endereço e ele reabre no
     * mesmo escopo. É o único recorte enviado ao servidor.
     */
    scopeFilter() {
      const { query } = this.$route;
      return {
        companiesIds: toArrayParam(query.companiesIds),
        regionalsIds: toArrayParam(query.regionalsIds),
        installationsIds: toArrayParam(query.installationsIds),
      };
    },
    grouping() {
      const value = this.$route.query.groupBy;
      return isValidGrouping(value) ? value : DEFAULT_GROUPING;
    },
    groupedByAsset() {
      return this.grouping === groupingLevels.ASSET;
    },
    chart() {
      const value = this.$route.query.chart;
      return isValidPanelChart(value) ? value : DEFAULT_PANEL_CHART;
    },
    /**
     * Visualização do Painel. O Sunburst ignora o Agrupamento: sua hierarquia é
     * fixa em Empresa → Instalação → Ativo.
     */
    view() {
      const value = this.$route.query.view;
      return isValidPanelView(value) ? value : DEFAULT_PANEL_VIEW;
    },
    isRadial() {
      return this.view === panelViews.RADIAL;
    },
    /**
     * O Agrupamento do Painel define o anel mais interno do Radial. Ativo não é
     * raiz possível ali, então cai no padrão.
     */
    sunburstRoot() {
      return normalizeSunburstRoot(this.grouping);
    },
    states() {
      return normalizeStateFilter(String(this.$route.query.states || '').split(','));
    },
    query() {
      return String(this.$route.query.q || '');
    },
    /**
     * Rolagem Automática, na URL como o resto: quem pendura a TV liga uma vez,
     * salva o endereço, e a tela reabre rolando.
     */
    autoScroll() {
      return this.$route.query.autoscroll === AUTO_SCROLL_ON;
    },
    groups() {
      return markChangedGroups(
        groupAssets(this.assets, this.grouping),
        this.changedKeys,
      );
    },
    /**
     * Filtro do Painel: aplicado depois da agregação, nunca no request. Filtrar
     * status no servidor removeria sensores de dentro do ativo e faria
     * o estado consolidado e a contagem mentirem.
     */
    filtered() {
      return applyPanelFilter(this.groups, {
        states: this.states,
        query: this.query,
      });
    },
    /**
     * Os mesmos Ativos da Grade, planificados. A decoração do Filtro do Painel
     * é idêntica em qualquer Agrupamento, então achatar os cartões devolve cada
     * Ativo exatamente uma vez, já com `matched` e `dimmed`.
     */
    filteredAssets() {
      return this.filtered.groups
        .reduce((assets, group) => assets.concat(group.assets), []);
    },
    gridClass() {
      return `communication-status-panel__grid--${this.grouping}`;
    },
    showGrid() {
      return !this.initialLoading && !this.isRadial && this.filtered.groups.length > 0;
    },
    /**
     * Muda sempre que a grade pode ter mudado de forma: a Rolagem Automática
     * mede as páginas de novo, sem reiniciar a pausa em curso.
     */
    layoutKey() {
      return [
        this.grouping,
        this.chart,
        this.query,
        this.states.join(','),
        this.lastUpdate,
      ].join('|');
    },
    refreshIntervalInSeconds() {
      return this.$store.getters.autoRefreshInterval;
    },
    ageInMs() {
      return this.lastUpdate === null ? null : this.now - this.lastUpdate;
    },
    /**
     * O painel se declara desatualizado em vez de continuar exibindo o último
     * render como se fosse atual.
     */
    degraded() {
      return this.sessionExpired
        || isStale(this.ageInMs, this.refreshIntervalInSeconds);
    },
  },
  created() {
    this.startClock();
    this.refresh().then(() => this.startAutoRefresh());
  },
  beforeDestroy() {
    this.stopClock();
    this.stopAutoRefresh();
  },
  methods: {
    /**
     * Agrupamento, gráfico e filtro vivem na URL para sobreviver a um reload.
     * `replace` em vez de `push` para não encher o histórico a cada clique.
     */
    updateUrl(changes) {
      const query = { ...this.$route.query, ...changes };

      Object.keys(query).forEach((key) => {
        if (query[key] === '' || query[key] === undefined || query[key] === null) {
          delete query[key];
        }
      });

      this.$router.replace({ query }).catch(() => null);
    },
    setAutoScroll(enabled) {
      this.updateUrl({ autoscroll: enabled ? AUTO_SCROLL_ON : '' });
    },
    onAutoScrollStatus(status) {
      this.autoScrollStatus = status;
    },
    startClock() {
      this.clockTimer = setInterval(() => {
        this.now = Date.now();
      }, CLOCK_INTERVAL_IN_MS);
    },
    stopClock() {
      if (this.clockTimer) {
        clearInterval(this.clockTimer);
        this.clockTimer = null;
      }
    },
    startAutoRefresh() {
      this.refreshTimer = setInterval(
        () => this.refresh(),
        this.refreshIntervalInSeconds * MS_IN_SECOND,
      );
    },
    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },
    /**
     * Um ciclo de atualização. O estado exibido só é substituído quando todas as
     * páginas chegam: falhou qualquer uma, o painel mantém o último conjunto
     * completo e deixa o relógio de idade envelhecer até degradar.
     */
    async refresh() {
      try {
        const items = await loadAllInstallations(this.scopeFilter);
        this.assets = buildAssets(items);
        this.summary = summarizeAssets(this.assets);

        // A pulsação dura até o próximo ciclo: quem mudou agora fica marcado
        // até a atualização seguinte recalcular a diferença.
        const states = snapshotStates(this.assets);
        this.changedKeys = diffStates(this.previousStates, states);
        this.previousStates = states;
        this.lastUpdate = Date.now();
        this.now = this.lastUpdate;
        this.sessionExpired = false;
      } catch (error) {
        if (!this.$store.getters.authenticated) {
          this.sessionExpired = true;
          // Sem sessão, novos ciclos só produziriam 401 em sequência, para sempre,
          // numa tela que ninguém está observando. O painel congela exibindo o
          // aviso, e a idade do dado continua correndo.
          this.stopAutoRefresh();
        }
      } finally {
        this.initialLoading = false;
      }
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

.communication-status-panel
  background: var(--panel-background)
  display: flex
  flex-direction: column
  // O rodapé da aplicação fica fora deste componente. Com 100vh cheios, o
  // documento passaria a rolar junto e apareceria uma segunda barra à direita.
  height: calc(100vh - #{$footer-height})
  overflow: hidden

  &__body
    flex: 1
    overflow-y: auto
    padding: 0.9rem 1.2rem

    // O Sunburst ocupa a área inteira e nao rola: rolagem num painel de TV
    // esconderia parte do gráfico sem que ninguém pudesse rolar até ela.
    &--chart
      display: flex
      overflow: hidden

    // Na Grade quem rola é a Rolagem Automática, que carrega o próprio respiro:
    // o corpo só entrega a altura.
    &--scroller
      display: flex
      flex-direction: column
      overflow: hidden
      padding: 0

  &__grid
    display: grid
    // A grade se adensa sozinha conforme a largura da TV, mantendo a ordem
    // estável: nenhum cartão muda de vizinho entre ciclos.
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr))
    gap: 0.7rem

    // No nível ativo cada cartão tem uma única faixa, então cabe mais por linha.
    &--asset
      grid-template-columns: repeat(auto-fill, minmax(180px, 1fr))

    // Empresas reúnem muitos ativos: cartões mais largos evitam
    // colunas altas e desalinhadas.
    &--company
      grid-template-columns: repeat(auto-fill, minmax(320px, 1fr))

  &__loader
    margin: 4rem auto

  &__empty
    color: var(--panel-text-muted)
    font-size: 1rem
    margin: 4rem auto
    text-align: center

  &__notice
    background: var(--panel-stale-notice)
    color: var(--panel-stale)
    font-weight: 700
    letter-spacing: 0.05em
    margin: 0
    padding: 0.5rem 1.2rem
    text-align: center
    text-transform: uppercase

    &--session
      padding: 1.2rem

      h2
        font-size: 1.4rem
        margin: 0 0 0.3rem

      p
        color: var(--panel-text)
        font-weight: 400
        letter-spacing: 0
        margin: 0
        text-transform: none

  // Dado velho dessatura a frota inteira: impossível confundir com frota saudável.
  &--stale .communication-status-panel__body
    filter: grayscale(0.75)
    opacity: 0.65

  // Celular: a página rola inteira. Com a altura presa em 100vh, cabeçalho e
  // controles tomariam quase tudo e sobraria uma fresta para a frota. O Radial,
  // que não rola, ganha altura própria.
  +media-max-sm()
    height: auto
    min-height: calc(100vh - #{$footer-height})
    overflow: visible

    &__body
      overflow: visible
      padding: 0.6rem 0.8rem

      &--chart
        height: 80vh

      &--scroller
        overflow: visible
        padding: 0

  // Até ~575px uma coluna só: os mínimos de 240/320px da grade estourariam a
  // largura. No nível Ativo, cartão de faixa única, cabem dois.
  +media-max-xs()
    &__grid,
    &__grid--company
      grid-template-columns: minmax(0, 1fr)

    &__grid--asset
      grid-template-columns: repeat(2, minmax(0, 1fr))
</style>
